// Live reads of the station's local database for React: the query runs now and again
// whenever a table it reads changes (a sync, a station action). Offline it keeps working:
// it only ever reads the device.
//
// The last rows of each query are kept for the station (UI7S-101): a view opened again draws
// straight away from them while its query runs, instead of a skeleton on every visit; only
// the station's first read of a query shows one. A result equal to the rows already shown
// keeps them as they are, so what is worked out from them (the plan check) isn't redone.

import type { SqlValue } from '@enp-suite/data';
import { useEffect, useState } from 'react';
import { getStationData } from './station-data-store.js';

export interface LiveQuery<T> {
  /** Undefined until the first result (show a skeleton). */
  rows: T[] | undefined;
  error: Error | undefined;
}

/** The last rows of each query, per station (a station paired again starts empty). */
const lastRows = new WeakMap<object, Map<string, readonly unknown[]>>();

const cacheKey = (sql: string, key: string) => `${sql}\u0000${key}`;

function cachedRows<T>(sql: string, key: string): T[] | undefined {
  const data = getStationData();
  return data ? (lastRows.get(data)?.get(cacheKey(sql, key)) as T[] | undefined) : undefined;
}

/** Whether two results hold the same rows (then the first is kept as it is). */
export function sameRows(a: readonly unknown[] | undefined, b: readonly unknown[]): boolean {
  if (a === b) return true;
  if (!a || a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/** `sql` over the local database, re-run on every change to the tables it reads. */
export function useLiveQuery<T>(
  sql: string,
  params: readonly SqlValue[] = [],
  enabled = true,
): LiveQuery<T> {
  const key = JSON.stringify(params);
  const [state, setState] = useState<LiveQuery<T>>(() => ({
    rows: enabled ? cachedRows<T>(sql, key) : undefined,
    error: undefined,
  }));
  useEffect(() => {
    const data = getStationData();
    if (!enabled || !data) return;
    const store = lastRows.get(data) ?? new Map<string, readonly unknown[]>();
    lastRows.set(data, store);
    const stop = data.engine.watch<T>(
      sql,
      JSON.parse(key) as SqlValue[],
      (rows) => {
        // The same rows as last time stay the same array, here and in the store.
        const k = cacheKey(sql, key);
        const prev = store.get(k) as T[] | undefined;
        const keep = prev && sameRows(prev, rows) ? prev : rows;
        store.set(k, keep);
        setState((s) => (!s.error && s.rows === keep ? s : { rows: keep, error: undefined }));
      },
      (error) => setState((s) => ({ rows: s.rows, error })),
    );
    return stop;
  }, [sql, key, enabled]);
  return state;
}
